import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { type ReactNode, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
} from "@rumbledirect/ui-v2";
import { formatDate } from "@rumbledirect/ui-v2/format";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { FieldGrid } from "@/dossiers/DetailSection";
import { getApiErrorStatus } from "@/lib/api/apiErrorStatus";
import {
  useGetAdminOrderStatusesBrief,
  useGetAdminOrdersId,
  useGetAdminOrdersIdWorkflowHistory,
  useGetAdminOrdersIdWorkflowTransitions,
} from "@/lib/api/generated/order/order";
import {
  finalOrderStatus,
  orderPhases,
  type OrderRow,
  type OrderStatusMeta,
} from "@/orders/order";
import { ORDER_DETAIL_COMPONENTS } from "@/orders/orderDetailComponents";
import { getOrderVehicleTitle } from "@/orders/orderFields";
import { useOrderDetailLayout } from "@/orders/useOrderDetailLayout";
import { useOrderStatusLookup } from "@/orders/useOrderStatusLookup";
import { DetailLayoutBody } from "@/viewConfiguration/DetailLayoutBody";

/** The permission the step buttons need; without it there are no steps to ask for. */
const TRANSITION_PERMISSION = "admin_order_workflow_transition";

const SKELETON_KEYS = ["a", "b", "c", "d"];

interface OrderDetailSearch {
  /** The open tab; its keys are the affiliate's, as served in the layout. */
  readonly tab: string | undefined;
}

export const Route = createFileRoute("/_authenticated/orders_/$orderId")({
  validateSearch: (search: Record<string, unknown>): OrderDetailSearch => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: OrderDetailPage,
});

function OrderDetailPage(): ReactNode {
  const { orderId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const user = useCurrentUser();
  const mayTransition = hasPermission(user, TRANSITION_PERMISSION);
  const { data, isPending, isError, refetch } = useGetAdminOrdersId(orderId);
  const layout = useOrderDetailLayout(orderId);
  const statuses = useOrderStatusLookup();
  // The phases of the workflow and where this order has been in them, which
  // together make its stepper.
  const phases = useGetAdminOrderStatusesBrief();
  const history = useGetAdminOrdersIdWorkflowHistory(orderId);
  // Only asked for by people who may take a step: without the permission the
  // request would answer 403 for a card that is not shown anyway.
  const transitions = useGetAdminOrdersIdWorkflowTransitions(
    orderId,
    undefined,
    { query: { enabled: mayTransition } },
  );
  // An order outside any workflow is answered with a 404 here too, a state
  // rather than a failure: it simply has no next steps to show.
  const hasNoWorkflow =
    transitions.isError && getApiErrorStatus(transitions.error) === 404;

  // Entering without a tab, or with one this layout does not carry, resolves
  // to the layout's default; writing that back as a replace keeps the URL
  // shareable without a history entry the back button has to walk past.
  const onTabResolved = useCallback(
    (nextTab: string | undefined) => {
      void navigate({ search: () => ({ tab: nextTab }), replace: true });
    },
    [navigate],
  );

  return (
    <div className="space-y-4">
      <Link
        to="/orders"
        search={{ page: 1, view: "open", status: undefined, query: undefined }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar bestellingen
      </Link>

      {/*
        A layout that cannot be fetched (anything but the 404 that means "none
        configured") is as fatal as an order that cannot be: the fallback
        layout could show cards the server never approved.
      */}
      {isError || layout.isError ? (
        <Card>
          <CardContent className="py-10 text-center">
            <div className="space-y-3" role="alert">
              <p className="text-muted-foreground">
                We konden deze bestelling niet laden.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void refetch();
                  void layout.refetch();
                }}
              >
                Opnieuw proberen
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <OrderDetailHeader
            order={data}
            isPending={isPending}
            statusMeta={
              data?.workflowStatus
                ? statuses.lookup.get(data.workflowStatus)
                : undefined
            }
          />

          {isPending || layout.isPending ? (
            <Card>
              <CardContent className="space-y-3 py-6">
                <span role="status" aria-live="polite" className="sr-only">
                  Bestelling wordt geladen…
                </span>
                {SKELETON_KEYS.map((key) => (
                  <Skeleton key={key} className="h-4 w-full max-w-md" />
                ))}
              </CardContent>
            </Card>
          ) : (
            // The rail leads on a phone, because the actions are what a
            // person comes to do; beside the tabs a step can be taken from
            // any of them.
            <DetailLayoutBody
              layout={layout.data}
              registry={ORDER_DETAIL_COMPONENTS}
              railFirst
              tab={tab}
              onTabChange={(nextTab) =>
                void navigate({ search: () => ({ tab: nextTab }) })
              }
              onTabResolved={onTabResolved}
              context={{
                order: data,
                actions: {
                  transitions: transitions.data?.transitions ?? [],
                  isPending: transitions.isPending,
                  isError: transitions.isError,
                  hasNoWorkflow,
                  finalStateId: finalOrderStatus(phases.data?.member)?.id,
                  onRetry: () => void transitions.refetch(),
                },
                phases: {
                  steps:
                    phases.data && history.data && data.workflowStatus
                      ? orderPhases(
                          phases.data.member,
                          history.data.auditLogs,
                          data.workflowStatus,
                        )
                      : [],
                  isPending: phases.isPending || history.isPending,
                  errorStatus: history.isError
                    ? (getApiErrorStatus(history.error) ?? 0)
                    : phases.isError
                      ? 0
                      : undefined,
                  onRetry: () => {
                    void phases.refetch();
                    void history.refetch();
                  },
                  hasAvailableTransitions: transitions.isSuccess
                    ? transitions.data.transitions.length > 0
                    : undefined,
                },
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function OrderDetailHeader({
  order,
  isPending,
  statusMeta,
}: {
  readonly order: OrderRow | undefined;
  readonly isPending: boolean;
  readonly statusMeta: OrderStatusMeta | undefined;
}): ReactNode {
  if (isPending || !order) {
    return <Skeleton className="h-28 w-full" />;
  }

  // An ordered car often has no plate yet, so the title falls back to the
  // car itself and then to the order number.
  const vehicleTitle = getOrderVehicleTitle(order);
  const title =
    order.licensePlate ||
    (vehicleTitle === "—" ? `Bestelling ${order.orderNumber}` : vehicleTitle);

  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {order.workflowStatus ? (
          <Badge variant={statusMeta?.color ?? "neutral"} size="sm">
            {statusMeta?.label ?? order.workflowStatus}
          </Badge>
        ) : null}
      </div>
      {/* What is needed to recognise the order, whichever tab is open. */}
      <FieldGrid
        fields={[
          { label: "Kenteken", value: order.licensePlate ?? undefined },
          { label: "Chassisnummer", value: order.vinNumber ?? undefined },
          { label: "Bestelnummer", value: String(order.orderNumber) },
          { label: "Dealer", value: order.dealershipName ?? undefined },
          {
            label: "Aangemaakt op",
            value: order.orderedAt ? formatDate(order.orderedAt) : undefined,
          },
          {
            label: "Verwachte levering",
            value: order.expectedDeliveryAt
              ? formatDate(order.expectedDeliveryAt)
              : undefined,
          },
          { label: "Medewerker", value: order.employee?.fullName ?? undefined },
        ]}
      />
    </Card>
  );
}
